// src/modules/app-views/invoices/pages/Init/modals/FiscalBootstrapModal/Step2.jsx
// *******************************************************************************
// STEP 2 - FISCAL IDENTIFICATION
// *******************************************************************************
// Second step: Review and complete fiscal identification data for the company.
// Reuses FiscalIdCard from fiscalConfig module.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect } from 'react';
import { Alert, Form } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import FiscalIdCard from 'modules/app-views/fiscalConfig/pages/Document/tabs/CertificateTab/FiscalIdCard';
import translateX from 'utils/translateX';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
// Required fields for fiscal identification
const FISCAL_ID_REQUIRED_FIELDS = [
  { path: ['fiscalData', 'taxRegime'], messageKey: 'inform_tax_regime' },
];

// *******************************************************************************
// STEP CONTENT COMPONENT
// *******************************************************************************
const Step2Content = ({ company, localCertificate, localStateRegistration, form }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  // Use local certificate or from company
  const displayCertificate = localCertificate || company?.fiscal?.certificate || null;

  // Check if services are enabled (from company data)
  const nfceEnabled = company?.fiscalData?.nfce?.isEnabled || false;
  const nfeEnabled = company?.fiscalData?.nfe?.isEnabled || false;
  const nfseEnabled = company?.fiscalData?.nfse?.isEnabled || false;

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Populate form with company fiscal data
  useEffect(() => {
    if (form && company) {
      const fiscalData = company?.fiscalData || {};
      const address = company?.address || {};

      // Use state registration from localStateRegistration (just retrieved via CCC/SEFAZ)
      // or fallback to company's existing fiscalData
      const stateRegistration =
        localStateRegistration?.stateRegistration || fiscalData.stateRegistration;

      // Use tax regime from localStateRegistration if available and not already set
      const taxRegime = fiscalData.taxRegime || localStateRegistration?.taxRegime;

      form.setFieldsValue({
        fiscalData: {
          taxRegime,
          stateRegistration,
          municipalRegistration: fiscalData.municipalRegistration,
          mainCnae: fiscalData.mainCnae,
          suframa: fiscalData.suframa,
          defaultIcmsCst: fiscalData.defaultIcmsCst,
          defaultIcmsCsosn: fiscalData.defaultIcmsCsosn,
          defaultPisCst: fiscalData.defaultPisCst,
          defaultCofinsCst: fiscalData.defaultCofinsCst,
          defaultIpiCst: fiscalData.defaultIpiCst,
          defaultIcmsRate: fiscalData.defaultIcmsRate,
          defaultPisRate: fiscalData.defaultPisRate,
          defaultCofinsRate: fiscalData.defaultCofinsRate,
          defaultIpiRate: fiscalData.defaultIpiRate,
        },
        address: {
          cityCode: address.cityCode,
        },
      });
    }
  }, [form, company, localStateRegistration]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 9. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ paddingTop: 8 }}>
      {/* Info Alert */}
      <Alert
        type="info"
        showIcon
        icon={<FileTextOutlined />}
        style={{ marginBottom: 24 }}
        title={translateX('review_fiscal_data')}
        description={translateX('review_fiscal_data_description')}
      />

      {/* Fiscal ID Card - Reusing from fiscalConfig */}
      <FiscalIdCard
        displayCertificate={displayCertificate}
        nfceEnabled={nfceEnabled}
        nfeEnabled={nfeEnabled}
        nfseEnabled={nfseEnabled}
      />
    </div>
  );
};

// *******************************************************************************
// STEP CONFIGURATION
// *******************************************************************************
const step2 = ({ company, localCertificate, localStateRegistration }) => ({
  stepTitle: 'fiscal_identification',
  stepTooltip: 'review_company_fiscal_data',
  content: ({ form }) => (
    <Step2Content
      company={company}
      localCertificate={localCertificate}
      localStateRegistration={localStateRegistration}
      form={form}
    />
  ),
  // Validation: check required fields
  validationFields: ['fiscalData.taxRegime'],
  onNext: async ({ form, goFocus }) => {
    // Manual validation of required fields
    const emptyFields = [];

    FISCAL_ID_REQUIRED_FIELDS.forEach(({ path, messageKey }) => {
      const value = form?.getFieldValue(path);
      const isEmpty = value === undefined || value === null || value === '';
      if (isEmpty) {
        emptyFields.push({ name: path, messageKey });
      }
    });

    if (emptyFields.length > 0) {
      const firstEmptyField = emptyFields[0];

      // Show warning message
      const { default: appService } = await import('services/appService');
      appService.message({
        flag: 'warning',
        text: firstEmptyField.messageKey,
        duration: 4,
      });

      // Mark fields as error
      const { default: translateX } = await import('utils/translateX');
      const fieldsToMark = emptyFields.map(({ name }) => ({
        name,
        errors: [translateX('required')],
      }));
      form.setFields(fieldsToMark);

      // Focus on first empty field
      if (goFocus && firstEmptyField.name) {
        goFocus(firstEmptyField.name.join('.'));
      }

      return false; // Prevent navigation
    }

    // Save fiscal data to backend before proceeding
    try {
      const { default: appService } = await import('services/appService');
      const { default: axiosService } = await import('services/axiosService');
      const { API } = await import('constants/Api');

      const entityId = company?.entityId || company?._id;
      if (!entityId) return true; // Allow navigation if no entityId (shouldn't happen)

      const formValues = form?.getFieldsValue(true) || {};

      // Save fiscal data
      await axiosService.put({
        url: API.ENTITIES.URL.replace(':_id?', entityId),
        data: {
          fiscalData: formValues.fiscalData,
          'address.cityCode': formValues.address?.cityCode,
        },
      });

      appService.message({
        flag: 's',
        text: 'fiscal_data_saved',
        duration: 2,
      });

      return true; // Allow navigation
    } catch (error) {
      console.error('[Step2] Error saving fiscal data:', error);

      const { default: appService } = await import('services/appService');
      appService.notification({
        flag: 'error',
        title: 'error',
        description: 'error_saving_fiscal_data',
      });

      return false; // Prevent navigation on error
    }
  },
});

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default step2;
